import { Grid, IconButton, List, ListItem, ListItemAvatar, ListItemText, Menu, MenuItem, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import * as api from "api/Knowledge";
import { DefaultListParameters } from 'assets/appConfig';
import BoxDrawer from 'components/BoxDrawer';
import IconButtonCancel from 'components/Buttons/IconButtonCancel';
import { SpinnerIndicator } from 'components/CallServer';
import IconEllipsisV from "components/Icons/IconEllipsisV";
import { JoinComponents } from 'components/JoinComponents';
import { GridEx } from 'components/MuiExtras/Grid/GridEx';
import MuiMessageBox from 'components/MuiExtras/MessageBox/MuiMessageBox';
import MuiConfirmation from 'components/MuiExtras/MuiConfirmation';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiPagination from 'components/MuiExtras/MuiPagination';
import MuiPaper from 'components/MuiExtras/MuiPaper';
import MuiColor from 'components/MuiExtras/Typography/MuiColor';
import UserProfileImageLink from 'components/UserProfileImageLink';
import { Formaters } from 'lib/Formarters';
import { SystemRoles } from 'models/ApiEnums';
import { CourseEnrollReviewDto, PagedResult } from 'models/ApiModels';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import Session from 'services/Session';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    data: PagedResult<CourseEnrollReviewDto>;
    lastError: any;
    loading: boolean;
    offset: number;
    menuAnchorEl: any;
    menuAnchorId: string;
    searchText: string;
    dialog: any;
    changed: boolean;
}

interface IProps {
    courseId: string;
    allowManageEnroll: boolean;
    onClose: (changed: boolean) => void;
}

type Props = IProps & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class CourseEnrollReviewDrawer extends React.PureComponent<Props, IState> {

    private searchRef: any = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            data: null,
            lastError: null,
            loading: true,
            offset: 0,
            menuAnchorEl: null,
            menuAnchorId: null,
            searchText: null,
            dialog: null,
            changed: false
        };
    }

    ///
    private loadData(offset: number) {
        this.setState({ loading: true, data: null });

        api.getCourseEnrollReviewList(
            this.props.courseId,
            this.state.searchText,
            offset,
            DefaultListParameters.drawerRecordsByPage
        )
            .then((data) => {
                this.setState({ data, loading: false, offset: offset });
            })
            .catch((error) => {
                this.setState({ loading: false, offset: 0, lastError: error });
            });
    }

    ///
    private handleOpenMenu(e: React.MouseEvent<HTMLElement>, id: string) {
        this.setState({ menuAnchorEl: e.currentTarget, menuAnchorId: id });
    }

    ///
    private handleCloseMenu() {
        this.setState({ menuAnchorEl: null });
    }

    ///
    private handleSearch(e: React.KeyboardEvent<HTMLDivElement>) {
        if (e.keyCode == 13) this.loadData(0);
    }

    ///
    private handleCancel = (item: CourseEnrollReviewDto) => {
        this.setState({
            dialog: <MuiConfirmation
                title={this.props.t("Views.Publication.Dialogs.CourseEnrollReviewDrawer.ConfirmationCancelTitle")}
                message={this.props.t("Views.Publication.Dialogs.CourseEnrollReviewDrawer.ConfirmationCancelMessage", { name: item.user.name })}
                onClose={
                    (result) => {
                        if (result) {
                            api.courseCancelEnroll(this.props.courseId, item.user.id)
                                .then((result) => {
                                    if (result) {
                                        this.setState({ dialog: null, changed: true }, () => this.loadData(0));
                                    }
                                })
                                .catch(() => {
                                    this.setState({ dialog: null });
                                });
                        }
                        else {
                            this.setState({ dialog: null });
                        }
                    }
                }
            />
        });
    };

    ///
    private renderList() {
        const { data, menuAnchorEl, menuAnchorId } = this.state;
        const isManager = this.props.allowManageEnroll && Session.hasRole(SystemRoles.CourseManager);
        return (
            <List dense disablePadding>
                {
                    data.items.map((v, index) => {
                        return (
                            <ListItem key={index} disableGutters alignItems="flex-start">
                                <ListItemAvatar>
                                    <UserProfileImageLink user={v.user} />
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <div>
                                            <MuiColor
                                                variant={
                                                    v.completedOn != null
                                                        ? "success"
                                                        : v.startedOn == null
                                                            ? "mutedDark"
                                                            : "black"
                                                }
                                            >
                                                <b>{v.user.name}</b>
                                            </MuiColor>
                                        </div>
                                    }
                                    secondary={
                                        <div>
                                            <Grid container justifyContent="space-between" alignItems="center">
                                                <Grid item>
                                                    {
                                                        isManager
                                                            ? <JoinComponents
                                                                components={[
                                                                    <span title={this.props.t("Views.Publication.Dialogs.CourseEnrollReviewDrawer.EnrolledOnTitle")}>{Formaters.relativeTime(v.enrolledOn)}</span>,
                                                                    <span title={this.props.t("Views.Publication.Dialogs.CourseEnrollReviewDrawer.ProgressTitle")}>{v.progress.toFixed(0)}%</span>
                                                                ]}
                                                            />
                                                            : <span title={this.props.t("Views.Publication.Dialogs.CourseEnrollReviewDrawer.EnrolledOnTitle")}>{Formaters.relativeTime(v.enrolledOn)}</span>
                                                    }
                                                </Grid>
                                                {
                                                    isManager && v.enrolledOn != null && v.completedOn == null &&
                                                    <Grid item>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => { this.handleOpenMenu(e, v.user.id); }}
                                                        >
                                                            <IconEllipsisV fixedWidth />
                                                        </IconButton>
                                                        <Menu
                                                            anchorEl={menuAnchorEl}
                                                            open={menuAnchorEl != null && v.user.id == menuAnchorId}
                                                            onClose={() => { this.handleCloseMenu(); }}
                                                        >
                                                            <MenuItem
                                                                key={v.user.id}
                                                                onClick={() => this.handleCancel(v)}
                                                            >
                                                                {this.props.t("Views.Publication.Dialogs.CourseEnrollReviewDrawer.ConfirmationCancelTitle")}
                                                            </MenuItem>
                                                        </Menu >
                                                    </Grid>
                                                }
                                            </Grid>
                                        </div>
                                    }
                                    primaryTypographyProps={{ variant: "body2", noWrap: true }}
                                    secondaryTypographyProps={{ variant: "body2" }}
                                />
                            </ListItem>
                        );
                    })
                }
            </List>
        );
    }

    ///
    public componentDidMount() {
        this.loadData(0);
    }

    ///
    public render() {
        const { onClose } = this.props;
        const { data, dialog, lastError, loading, offset, changed } = this.state;

        return (
            <BoxDrawer
                open
                overflow="hidden"
                onClose={() => onClose(changed)}
                title={this.props.t("Views.Publication.Dialogs.CourseEnrollReviewDrawer.BoxTitle")}
            >
                {dialog}

                <MuiPaper dense fullHeight elevation={0}>

                    <GridEx container direction="column" wrap="nowrap" fullHeight>

                        <GridEx>
                            <MuiContainer>
                                <TextField
                                    fullWidth
                                    placeholder={this.props.t("Common.Input.Placeholder.SearchFor")}
                                    value={this.state.searchText}
                                    onChange={() => this.setState({ searchText: this.searchRef.value })}
                                    onKeyDown={(e) => this.handleSearch(e)}
                                    onBlur={() => this.loadData(0)}
                                    inputRef={(ref) => this.searchRef = ref}
                                    InputProps={
                                        {
                                            startAdornment: <SearchIcon />,
                                            endAdornment: this.state.searchText != ""
                                                ? <IconButtonCancel
                                                    size="small"
                                                    onClick={
                                                        () => {
                                                            this.setState({ searchText: "" }, () => this.loadData(0));
                                                        }
                                                    } />
                                                : null
                                        }
                                    }
                                />
                            </MuiContainer>
                        </GridEx>

                        <GridEx item xs overflow="auto">

                            {
                                lastError
                                    ? <MuiMessageBox variant="error" message={this.props.t("Views.Publication.Dialogs.CourseEnrollReviewDrawer.LoadError")} />
                                    : loading
                                        ? <SpinnerIndicator />
                                        : data.total == 0
                                            ? <MuiMessageBox variant="info" message={this.props.t("Views.Publication.Dialogs.CourseEnrollReviewDrawer.EmptyList")} />
                                            : data.items && data.total > 0
                                                ? this.renderList()
                                                : null
                            }

                        </GridEx>

                        {
                            !lastError && !loading && data.items && data.total > 0 &&
                            <GridEx item>
                                <MuiPagination
                                    limit={DefaultListParameters.drawerRecordsByPage}
                                    offset={offset}
                                    total={data.total}
                                    onClick={(e, offset) => this.loadData(offset)}
                                />
                            </GridEx>
                        }
                    </GridEx>

                </MuiPaper>

            </BoxDrawer>
        );
    }
}

export default withTranslation()(CourseEnrollReviewDrawer);